// ─────────────────────────────────────────────────────────────────────────────
// Script Grader — deterministic, rule-based TypeScript implementation of
// Tim's real grading standard (per B-script-grader-workflow-v0.1.md and the
// 7 supporting expertise docs, 01-07, in creator-script-3/).
//
// This intentionally does NOT call any LLM — Ironside has no AI backend
// integration anywhere else in the app, so this stays consistent: string
// and pattern checks only, fully deterministic on the same input.
//
// Source docs (for anyone auditing this against the real spec):
//   B-script-grader-workflow-v0.1.md  — the grading procedure + grade assembly
//   07-hook-expertise.md              — the 4-channel hook check + STIC beats
//   02-tim-qc-brain.md                — brand-recognition rule, grade scale
//   01-brand-strategy-guide.md        — brand-fit check
//   05-creator-competencies.md        — creator-type fit
//   06-lessons-learned-db.md          — named failure patterns
//   04-competitor-analysis.md         — inspo link check
// ─────────────────────────────────────────────────────────────────────────────

// v0.3.0: grading moved from a letter scale to a 0-100 score, and 80 is good
// enough to be submitted for feedback/review by the AM. SUBMIT_THRESHOLD is the
// single source of truth
// for that gate; the editor UI reads this constant rather than a hardcoded
// 80 so the two can never drift.
export const SUBMIT_THRESHOLD = 80
export type TrafficLight = "green" | "orange" | "red"
export type CheckStatus = "pass" | "fail" | "heuristic" | "skipped"

/** score >= 80 → green (submit-ready). 55-79 → orange (getting there, not yet submittable). <55 → red (needs major rework). */
export function trafficLightForScore(score: number): TrafficLight {
  if (score >= SUBMIT_THRESHOLD) return "green"
  if (score >= 55) return "orange"
  return "red"
}

// Field order matches the document, top to bottom: Title, Hook, Script angle,
// Visual concept, Inspiration videos (plural — a creator can attach more than
// one), Script, Call to action, Caption, Hashtags, Value prop. Hook is a
// first-class field — "it's THAT important" (80% of the grade). The Script may
// repeat the hook's words at its start; that's fine and expected.
//
// scriptAngle is the primary creative field, added above visualConcept and
// inspoLinks because those two are its *supporting evidence*: the angle is the
// take, the visual is how that take looks in frame 1, and the inspo link is the
// comparable it borrows from. They were previously two peer fields with no
// stated relationship, which left the take itself written down nowhere.
//
// cta and valueProp are TWO fields, and the split is the point. They were one
// slot named "Value prop / takeaway" (relabeled from "CTA" on 2026-07-04: a
// hard sales CTA is the wrong tone in a new brand's first two weeks).
// One slot with two names collected three different things: real asks ("Comment
// PSYLLIUM if you want my full breakdown"), closing punch lines ("It's not you.
// It's fibre."), and statements of intent about the viewer ("The viewer clearly
// understands the benefits of the supplement"). H15 grades *the register of an
// ask*, so the latter two hit its "green by design when no product CTA exists"
// band and scored green-by-default — graded, but not usefully.
//
// In historical Google Docs, a literal "CTA:" heading is still an ask and the
// corpus segmenter maps it to `cta`. An app draft keeps its ask under `cta` and
// its takeaway under `valueProp`, by source, not by reading the prose.
export interface ScriptFields {
  brandName: string
  creatorName: string
  title: string
  hook: string
  scriptAngle: string
  visualConcept: string
  inspoLinks: string[]
  scriptBody: string
  cta: string
  caption: string
  hashtags: string
  valueProp: string
}

export interface HighlightSpan {
  start: number
  end: number
  kind: "hard-fail" | "antipattern"
  reason: string
}

export interface ExpertiseFinding {
  key: string
  label: string
  status: CheckStatus
  summary: string
  detail: string
  /**
   * Per-doc sub-score (0-100) — surfaced in the editor's "Deep dive"
   * expander, collapsed by default. Deterministic
   * from status: fail -> 50 (or 40 for hook-expertise, the doc that still
   * carries the grade's one real hard block), heuristic -> 74 (can't fully
   * verify, so it can't score as high as a confirmed pass), pass -> 88,
   * skipped -> null (insufficient data to assess, not a failure).
   */
  subGrade: number | null
}

export type SticBeat = "setup" | "tension" | "intensify" | "climax"

export interface SticBeatMap {
  beat: SticBeat
  label: string
  detected: boolean
  timingHint: string
}

export interface GradeResult {
  /** 0-100. SUBMIT_THRESHOLD (80) is the line for "good enough to submit for AM/brand review." */
  grade: number
  trafficLight: TrafficLight
  hardBlocked: boolean
  hardBlockReasons: string[]
  whatsGood: string
  fix1: string
  fix2: string
  hookHighlights: HighlightSpan[]
  scriptHighlights: HighlightSpan[]
  expertiseFindings: ExpertiseFinding[]
  sticBeats: SticBeatMap[]
  pathToA: string[]
  hookRewrites: string[]
  /**
   * Display-only aggregate of the 7 expertiseFindings sub-scores. This is
   * NOT the grading algorithm (that's the hard-block + channel-count +
   * STIC-deduction pipeline in gradeScript, unchanged) — it's a plain
   * average of the 7 sub-scores shown alongside `grade` so a reader can see
   * how the 01-07 docs individually score this script.
   */
  subGradeAverage: number | null
}

// ─── Shared detection helpers ──────────────────────────────────────────────

// CTA-like phrases that belong in the CTA field, not spoken in the script.
// Pulled from creator-brief-2026-07-02.md Tab 2 ("Save this for later," "Comment X")
// and 07-hook-expertise.md antipattern #5 ("CTA inside the spoken script").
const CTA_PHRASES = [
  "link in bio", "links in bio", "comment", "save this", "save for later",
  "follow for more", "shop now", "swipe up", "tap the link", "click the link",
  "check the link", "get yours", "order now", "buy now", "add to cart",
]

function findPhraseSpans(text: string, phrases: string[]): HighlightSpan[] {
  const spans: HighlightSpan[] = []
  const lower = text.toLowerCase()
  for (const phrase of phrases) {
    let idx = lower.indexOf(phrase)
    while (idx !== -1) {
      spans.push({
        start: idx,
        end: idx + phrase.length,
        kind: "antipattern",
        reason: `CTA-style language ("${text.slice(idx, idx + phrase.length)}") belongs in the CTA field, not spoken in the Script — per 07-hook-expertise.md antipattern #5.`,
      })
      idx = lower.indexOf(phrase, idx + phrase.length)
    }
  }
  return spans
}

// v0.2.1: Hook is a first-class field again — all hook checks run directly
// against f.hook (no more first-sentence-of-Script extraction). Highlight
// spans returned by hook checks index into the Hook field's own text.

/** First ~8 words of the hook, matching the spec's "first several words" brand-recognition window. */
function firstWords(text: string, n: number): string {
  return text.trim().split(/\s+/).slice(0, n).join(" ")
}

function brandNameFirstCheck(hook: string, brandName: string): HighlightSpan | null {
  if (!brandName.trim() || !hook.trim()) return null
  const window = firstWords(hook, 8)
  const idx = window.toLowerCase().indexOf(brandName.trim().toLowerCase())
  if (idx === -1) return null
  // Map the index found in the trimmed word-window back to the real hook string.
  const realIdx = hook.toLowerCase().indexOf(brandName.trim().toLowerCase())
  if (realIdx === -1 || realIdx > window.length) return null
  return {
    start: realIdx,
    end: realIdx + brandName.trim().length,
    kind: "hard-fail",
    reason: `Brand-name-first hook. Hard-fail per Tim's QC standard: "assume the viewer has never heard of the brand." Reframe around the felt need instead — e.g. "What does Mom Agenda have to offer?" → "What does a good mom's daily bag actually need?"`,
  }
}

const CONTRAST_WORDS = ["but", "however", "except", "problem is", "the catch", "turns out", "yet"]
const ESCALATION_WORDS = ["then", "worse", "harder", "even more", "turns out", "kept", "still wasn't", "next"]
const CLIMAX_WORDS = ["finally", "the result", "turns out", "here's how", "here's what happened", "the difference", "mission accomplished", "undeniable"]

function containsAny(text: string, words: string[]): boolean {
  const lower = text.toLowerCase()
  return words.some((w) => lower.includes(w))
}

/** The editor's Hashtags section warns above this; H8 scores it. */
export const HASHTAG_CAP = 3

export function countHashtags(hashtags: string): number {
  const matches = hashtags.match(/#[\w]+/g)
  if (matches && matches.length > 0) return matches.length
  // fall back to comma/space separated tokens if no literal # present
  return hashtags.trim() ? hashtags.trim().split(/[,\s]+/).filter(Boolean).length : 0
}

// ─── Step 1: Hook check (~80% of grade) ────────────────────────────────────

interface HookCheckResult {
  brandNameFirstFail: boolean
  lineClean: boolean
  visualClean: boolean
  captionClean: boolean
  cleanChannels: number // of 3 script-stage-assessable channels: line, visual-concept, caption
  notes: string[]
}

function runHookCheck(f: ScriptFields): HookCheckResult {
  const notes: string[] = []

  const brandSpan = brandNameFirstCheck(f.hook, f.brandName)
  const brandNameFirstFail = !!brandSpan

  // Line: no ramp-up, names stakes/outcome fast, teases without spoiling.
  const rampUpPhrases = ["hey guys", "in this video", "today i'm going to show you", "so today"]
  const hasRampUp = containsAny(f.hook, rampUpPhrases)
  const hookWordCount = f.hook.trim().split(/\s+/).filter(Boolean).length
  const lineClean = !brandNameFirstFail && !hasRampUp && hookWordCount >= 4
  if (hasRampUp) notes.push('Hook opens with a ramp-up phrase ("hey guys" / "in this video") — the hook IS line one, per creator-brief.md Tab 1.')

  // Visual concept: real, specific plan (not a placeholder), ideally with an inspo link backing it.
  const placeholderPhrases = ["tbd", "figure out later", "something visual", "n/a", "not sure yet"]
  const visualIsPlaceholder = containsAny(f.visualConcept, placeholderPhrases) || f.visualConcept.trim().length < 15
  const visualClean = !visualIsPlaceholder
  if (visualIsPlaceholder) notes.push("Visuals concept reads as a placeholder rather than a real, specific plan — per B-script-grader-workflow-v0.1.md Step 1.")

  // Caption channel: caption itself shouldn't open like a sales pitch, a
  // specific CTA should exist (in the CTA field, not folded into caption
  // prose), and hashtags are capped at 3.
  const hashtagCount = countHashtags(f.hashtags)
  const captionStartsWithSalesPitch = /^(buy|shop|our|introducing|we are excited)/i.test(f.caption.trim())
  const genericCta = /^(follow for more|check us out)\.?$/i.test(f.cta.trim())
  const ctaMissing = !f.cta.trim()
  const captionClean = !captionStartsWithSalesPitch && hashtagCount <= 3 && !ctaMissing && !genericCta
  if (captionStartsWithSalesPitch) notes.push('Caption opens like a sales pitch instead of a micro-CTA ("Save this for later," "Comment X") — per creator-brief.md Tab 2.')
  if (hashtagCount > 3) notes.push(`${hashtagCount} hashtags found — cap is 3. More reads as spammy, per creator-brief.md Tab 2 and 07-hook-expertise.md Channel 4.`)
  if (ctaMissing) notes.push("No CTA provided — the caption channel needs a specific ask (\"Save this for later,\" \"Comment X\"), per creator-brief.md Tab 2.")
  if (genericCta) notes.push('CTA is generic ("follow for more") — Tim\'s formula wants something specific and easy, not generic. See C-performance-metrics.md Metric 2.')

  const cleanChannels = [lineClean, visualClean, captionClean].filter(Boolean).length

  return { brandNameFirstFail, lineClean, visualClean, captionClean, cleanChannels, notes }
}

// ─── Step 2: STIC beat check (heuristic — needs human confirmation) ────────

interface StieResult {
  setupOk: boolean // hook exists at all
  tensionOk: boolean
  intensifyOk: boolean
  climaxOk: boolean
  weakBeats: string[]
}

function runSticCheck(f: ScriptFields): StieResult {
  const setupOk = f.hook.trim().length > 0
  const tensionOk = containsAny(f.scriptBody, CONTRAST_WORDS)
  const intensifyOk = containsAny(f.scriptBody, ESCALATION_WORDS)
  const climaxOk = containsAny(f.scriptBody, CLIMAX_WORDS) || /[.!]\s*$/.test(f.scriptBody.trim())

  const weakBeats: string[] = []
  if (!setupOk) weakBeats.push("Setup")
  if (!tensionOk) weakBeats.push("Tension")
  if (!intensifyOk) weakBeats.push("Intensify")
  if (!climaxOk) weakBeats.push("Climax")

  return { setupOk, tensionOk, intensifyOk, climaxOk, weakBeats }
}

// ─── Step 3: Format compliance (hard checks) ───────────────────────────────

interface FormatResult {
  hashtagCount: number
  hashtagOverCap: boolean
  ctaInScriptBody: boolean
  ctaSpans: HighlightSpan[]
}

function runFormatCheck(f: ScriptFields): FormatResult {
  const hashtagCount = countHashtags(f.hashtags)
  const hashtagOverCap = hashtagCount > 3
  const ctaSpans = findPhraseSpans(f.scriptBody, CTA_PHRASES)
  const ctaInScriptBody = ctaSpans.length > 0
  return { hashtagCount, hashtagOverCap, ctaInScriptBody, ctaSpans }
}

// ─── Step 4: Inspo link check (soft flag) ──────────────────────────────────
// Inspiration videos is a plural field (a creator can attach more than
// one comparable). v0.3.0: downgraded from a hard block to a soft deduction:
// the field is optional, not gate-worthy. The source docs
// (04-competitor-analysis.md, B-script-grader-workflow-v0.1.md) still describe
// it as a hard block; how the AMs who run this workflow actually use it
// overrides the doc for now. Still checked and still costs points — just
// doesn't cap the score the way brand-name-first does.

function runInspoCheck(f: ScriptFields): { missing: boolean } {
  return { missing: f.inspoLinks.filter((l) => l.trim()).length === 0 }
}

// ─── Grade assembly (Step 8 of B-script-grader-workflow-v0.1.md, adapted
// to a 0-100 score) ────────────────────────────────────────────────────────
//
// Base score from clean-channel count (Step 8.2 concept, preserved):
//   3/3 clean channels -> 92 (top-tier candidate)
//   2/3 clean           -> 76 (close, needs one more channel clean)
//   1/3 or 0/3 clean     -> 58 / 40
// STIC deduction: -8 per weak/missing beat (Step 8.3 concept — used to be
// a full letter-tier drop, now a point deduction of similar real weight).
// CTA-in-script: -6, same as before, unless already reflected via a weak
// Climax beat (avoids double-penalizing the same underlying issue).
// Inspo-missing: -6, soft (see runInspoCheck above) — no longer a hard cap.
// Brand-name-first hook: still the one real hard block (Step 8.1) — caps
// the final score at 45, well below SUBMIT_THRESHOLD, regardless of
// anything else scoring clean.

function runInspoCheckPenalty(missing: boolean): number {
  return missing ? -6 : 0
}

export function gradeScript(f: ScriptFields): GradeResult {
  const hardBlockReasons: string[] = []

  const hook = runHookCheck(f)
  const stic = runSticCheck(f)
  const format = runFormatCheck(f)
  const inspo = runInspoCheck(f)

  if (hook.brandNameFirstFail) {
    hardBlockReasons.push('Hook is brand-name-first — assumes recognition an SMB brand doesn\'t have. Hard fail per Tim\'s QC standard, regardless of everything else.')
  }

  const hardBlocked = hardBlockReasons.length > 0

  // Base score from clean-channel count.
  let grade: number =
    hook.cleanChannels === 3 ? 92 :
    hook.cleanChannels === 2 ? 76 :
    hook.cleanChannels === 1 ? 58 : 40

  // STIC deduction: -8 per weak/missing beat.
  grade -= stic.weakBeats.length * 8

  // CTA-in-script — treated as a weak Climax-adjacent format failure, -6 unless already reflected.
  if (format.ctaInScriptBody && !stic.weakBeats.includes("Climax")) {
    grade -= 6
  }

  // Inspo missing — soft deduction only, as above.
  grade += runInspoCheckPenalty(inspo.missing)

  // The one real hard block: brand-name-first caps the score at 45, well below the 80 submit threshold.
  if (hardBlocked) {
    grade = Math.min(grade, 45)
  }

  grade = Math.max(0, Math.min(100, Math.round(grade)))

  const trafficLight = trafficLightForScore(grade)

  // ── Build the 3-bullet Slack-style output (what's good / fix 1 / fix 2) ──
  const whatsGood = buildWhatsGood(f, hook, stic, format, inspo)
  const { fix1, fix2 } = buildFixes(f, hook, stic, format, inspo, hardBlocked)

  // ── Highlights ──
  const hookHighlights: HighlightSpan[] = []
  const brandSpan = brandNameFirstCheck(f.hook, f.brandName)
  if (brandSpan) hookHighlights.push(brandSpan)

  const scriptHighlights: HighlightSpan[] = [...format.ctaSpans]

  // ── 7 expertise-section findings ──
  const expertiseFindings = buildExpertiseFindings(f, hook, stic, format, inspo)

  // ── STIC beat map ──
  const sticBeats: SticBeatMap[] = [
    { beat: "setup",     label: "Setup",     detected: stic.setupOk,     timingHint: "sec 1-3" },
    { beat: "tension",   label: "Tension",   detected: stic.tensionOk,   timingHint: "sec 4-10" },
    { beat: "intensify", label: "Intensify", detected: stic.intensifyOk, timingHint: "sec 10-25" },
    { beat: "climax",    label: "Climax",    detected: stic.climaxOk,    timingHint: "final beat" },
  ]

  // ── Path to A (delta-to-target) ──
  const pathToA = buildPathToA(f, hook, stic, format, inspo, grade)

  // ── Hook rewrite suggestions — only meaningful when the hook hard-fails ──
  const hookRewrites = hook.brandNameFirstFail ? buildHookRewrites(f) : []

  // ── Sub-grade average (display only) ──
  const subGradeAverage = averageSubGrades(expertiseFindings)

  return {
    grade,
    trafficLight,
    hardBlocked,
    hardBlockReasons,
    whatsGood,
    fix1,
    fix2,
    hookHighlights,
    scriptHighlights,
    expertiseFindings,
    sticBeats,
    pathToA,
    hookRewrites,
    subGradeAverage,
  }
}

// ─── Sub-grade helpers ──────────────────────────────────────────────────────

const SUBGRADE_FOR_STATUS: Record<CheckStatus, number | null> = {
  pass: 88,
  heuristic: 74,
  fail: 50,
  skipped: null,
}

function averageSubGrades(findings: ExpertiseFinding[]): number | null {
  const grades = findings.map((f) => f.subGrade).filter((g): g is number => g !== null)
  if (grades.length === 0) return null
  return Math.round(grades.reduce((s, g) => s + g, 0) / grades.length)
}

// ─── Path to 80: shortest concrete delta from current score to SUBMIT_THRESHOLD/Green ──
// Distinct from fix1/fix2 (general suggestions) — this is ordered
// specifically by what's actually blocking submission, cheapest fix first.
function buildPathToA(
  f: ScriptFields, hook: HookCheckResult, stic: StieResult, format: FormatResult,
  inspo: { missing: boolean }, grade: number
): string[] {
  if (grade >= SUBMIT_THRESHOLD) {
    return [`Already at ${grade}/100 — clears the ${SUBMIT_THRESHOLD} bar, submit it.`]
  }

  const steps: string[] = []

  // Hard blocks are always the first and fastest fix, per B-script-grader-workflow-v0.1.md
  // Step 9: "Often one hard block is faster to resolve than five separate hook rewrites."
  if (inspo.missing) {
    steps.push("Attach a real inspiration link (recommended, not required, but it's worth points).")
  }
  if (hook.brandNameFirstFail) {
    steps.push(`Rewrite the hook to lead with the felt need instead of "${f.brandName || "the brand name"}." See the hook rewrite suggestions below.`)
  }
  if (stic.weakBeats.includes("Tension")) {
    steps.push("Add a Tension beat — name the stakes before the payoff.")
  }
  if (stic.weakBeats.includes("Intensify")) {
    steps.push("Add an Intensify beat — escalate the obstacle before the Climax.")
  }
  if (stic.weakBeats.includes("Climax")) {
    steps.push("Land a clear Climax and cut — no extra wrap-up.")
  }
  if (format.ctaInScriptBody) {
    steps.push("Move the CTA out of the Script field and into the CTA field.")
  }
  if (!hook.visualClean) {
    steps.push("Replace the placeholder visuals concept with a real, specific plan.")
  }
  if (format.hashtagOverCap) {
    steps.push(`Cut hashtags from ${format.hashtagCount} to 3 max.`)
  }

  if (steps.length === 0) {
    steps.push("Tighten the hook line and caption — you're close, but not yet 3/3 clean on the script-stage channels.")
  }

  return steps
}

// ─── Hook rewrite suggestions ───────────────────────────────────────────────
// Only generated when the brand-name-first hard-fail fires. Template-
// based string generation using the 3 real role-model patterns named in
// 07-hook-expertise.md and creator-brief.md Tab 1 (Felt-Need Question,
// Stakes-First Cold Open, Named Objection), slotted with this script's own
// brand/category content — not free-generated, still fully deterministic.
function buildHookRewrites(f: ScriptFields): string[] {
  const brand = f.brandName.trim() || "the brand"
  // Try to recover a category/need phrase from the hook itself, stripping the
  // brand name and common lead-in verbs ("have to offer", "just dropped", etc.)
  let stripped = f.hook
    .replace(new RegExp(brand, "gi"), "")
    .replace(/\b(have to offer|just dropped|just released|announces|presents)\b/gi, "")
    .replace(/[?.!]/g, "")
    .trim()
  if (!stripped) stripped = "this category"

  return [
    `Felt-Need Question: "What does [the actual need ${brand} solves] actually need?" — e.g. the Mom Agenda correction: "What does a good mom's daily bag actually need?" Bring ${brand} in as the answer, not the subject.`,
    `Stakes-First Cold Open: open on the risk or constraint before naming ${brand} at all — e.g. "Most people get this wrong, and it costs them [specific outcome]." Let the product resolve the tension instead of opening on it.`,
    `Named Objection: lead with the doubt a viewer already has — e.g. "I didn't think ${stripped.toLowerCase() || "this"} was worth it, until..." — then let ${brand} be the payoff of overcoming that doubt.`,
  ]
}

// ─── Output copy builders ───────────────────────────────────────────────────

function buildWhatsGood(f: ScriptFields, hook: HookCheckResult, stic: StieResult, format: FormatResult, inspo: { missing: boolean }): string {
  if (!hook.brandNameFirstFail && hook.lineClean) {
    return `Hook opens on the felt need with no ramp-up — no assumed brand recognition. Clean per Tim's QC standard.`
  }
  if (!inspo.missing) {
    return `Inspiration link is attached, giving a clear sense of what this script is replicating.`
  }
  if (hook.captionClean) {
    return `Caption leads with a micro-CTA rather than a sales pitch, and hashtags are within the 3-tag cap.`
  }
  return `Script has all required fields filled in — a full pass can run against every check.`
}

function buildFixes(
  f: ScriptFields, hook: HookCheckResult, stic: StieResult, format: FormatResult,
  inspo: { missing: boolean }, hardBlocked: boolean
): { fix1: string; fix2: string } {
  const fixes: string[] = []

  if (hook.brandNameFirstFail) {
    fixes.push(`Rewrite the hook to open with the felt need, not "${f.brandName}." Tim's real correction on this exact pattern: swap "What does ${f.brandName || "[Brand]"} have to offer?" for "What does a good mom's daily bag actually need?" — bring the brand in as the payoff, not the premise. Target: 60% of viewers still watching at second 4.`)
  }
  if (inspo.missing) {
    fixes.push(`Attach a real inspiration link — an actual competitor/comparable video that performed well, not a text description. Optional, but it's worth real points and gives the AM useful context.`)
  }
  if (format.hashtagOverCap) {
    fixes.push(`Cut hashtags from ${format.hashtagCount} down to 3 max. More reads as spammy per creator-brief.md Tab 2.`)
  }
  if (format.ctaInScriptBody) {
    fixes.push(`Move the CTA-style language out of the Script field and into the CTA field. The spoken script should end on the Climax payoff, then cut — no CTA, no wrap-up.`)
  }
  if (stic.weakBeats.includes("Tension")) {
    fixes.push(`Add a real Tension beat (sec 4-10): name the stakes or constraint before the payoff. Try a contrast word ("but," "the problem is") to signal the turn. (In the newer Hook → Retain → Reward vocabulary, Tension + Intensify together are the Retain phase.)`)
  }
  if (stic.weakBeats.includes("Intensify")) {
    fixes.push(`Add an Intensify beat (sec 10-25) — the most common structural miss. Escalate the obstacle before the Climax; Setup → Tension → Climax with nothing in between reads flat. (Note: short trend videos, 6-20s, only need hook + payoff — confirm the intended video type before forcing this beat.)`)
  }
  if (stic.weakBeats.includes("Climax")) {
    fixes.push(`Land a clear Climax — a specific resolution, then cut. No extra wrap-up after the payoff hits. (Hook → Retain → Reward: this is the Reward.)`)
  }
  if (!hook.visualClean) {
    fixes.push(`Replace the placeholder visuals concept with a real, specific plan — ideally backed by the inspo link, not a vague description.`)
  }

  while (fixes.length < 2) {
    fixes.push(`Read the hook aloud: if it sounds like a script, tighten it until it sounds like a story you'd tell a friend.`)
  }

  return { fix1: fixes[0], fix2: fixes[1] }
}

// ─── 7 expertise-section findings for the expandable cards ────────────────

function buildExpertiseFindings(
  f: ScriptFields, hook: HookCheckResult, stic: StieResult, format: FormatResult, inspo: { missing: boolean }
): ExpertiseFinding[] {
  const findings: ExpertiseFinding[] = []

  // 1. Brand Strategy (01-brand-strategy-guide.md)
  const hasBrandDocSignal = f.brandName.trim().length > 0
  const brandStrategyStatus: CheckStatus = !hasBrandDocSignal ? "skipped" : hook.brandNameFirstFail ? "fail" : "heuristic"
  findings.push({
    key: "brand-strategy",
    label: "Brand Strategy",
    status: brandStrategyStatus,
    subGrade: SUBGRADE_FOR_STATUS[brandStrategyStatus],
    summary: !hasBrandDocSignal
      ? "No brand name provided — can't check ICP/tone fit."
      : hook.brandNameFirstFail
      ? `Hook opens on "${f.brandName}" instead of the felt need — this is the exact antipattern logged for Mom Agenda, July 1.`
      : "No brand-name-first violation detected. Felt-need vs. brand-doc ICP match is a human judgment call.",
    detail: !hasBrandDocSignal
      ? "01-brand-strategy-guide.md requires pulling the client's brand doc before grading brand fit. Fill in Brand name to run the mechanical brand-recognition check; the deeper ICP/tone match still needs a human against the real brand doc."
      : hook.brandNameFirstFail
      ? `The single sharpest test in 01-brand-strategy-guide.md: "is the felt need in the hook the SAME felt need the brand doc identifies, or has the creator substituted a generic one?" Leading with the brand name skips the felt need entirely. Logged antipattern: "Mom Agenda, July 1 session: hook opened with 'What does Mom Agenda have to offer?' — assumed brand recognition."`
      : `Heuristic, needs human confirmation — this checker can only confirm the brand-name-first mechanical fail is absent. Whether the hook's felt need actually matches ${f.brandName}'s stated ICP (tone, excluded categories, competitor mentions) requires the real brand doc, per 01-brand-strategy-guide.md.`,
  })

  // 2. Tim's QC Brain / Hook standard (02-tim-qc-brain.md)
  const timQcStatus: CheckStatus = hook.brandNameFirstFail ? "fail" : hook.cleanChannels >= 2 ? "pass" : "fail"
  findings.push({
    key: "tim-qc-brain",
    label: "Tim's QC Brain / Hook Standard",
    status: timQcStatus,
    subGrade: SUBGRADE_FOR_STATUS[timQcStatus],
    summary: hook.brandNameFirstFail
      ? "Fails Tim's #1 rule: assume zero brand recognition. Hard-fail regardless of everything else."
      : `${hook.cleanChannels}/3 script-stage channels clean (line, visuals-concept, caption).`,
    detail: `02-tim-qc-brain.md: "Hook is 80% of the score. Four channels firing together — the line, the visual, the audio/tonality, the caption." At script stage only 3 of 4 are assessable (audio/tonality is a shoot-day check). Combine rule: 3/3 clean → A- candidate, 2/3 → caps B+, 1 or fewer → caps C or below. Current org baseline is B-minus; the lever to move it is hook quality specifically.`,
  })

  // 3. TikTok Trends (03-tiktok-trends.md)
  findings.push({
    key: "tiktok-trends",
    label: "TikTok Trends",
    status: "skipped",
    subGrade: null,
    summary: "No live trend snapshot available this cycle — trend check unavailable.",
    detail: `03-tiktok-trends.md: "Current trend snapshot: PENDING FIRST PULL — no live trend data captured yet." Per the doc's own rule, a trend section older than 2 weeks (or, as here, never pulled) should not be graded against — this is a soft flag, not a grade-blocker on its own. Re-check once Acsah's pod trend pull is live.`,
  })

  // 4. Competitor Analysis (04-competitor-analysis.md)
  const competitorStatus: CheckStatus = inspo.missing ? "fail" : "heuristic"
  findings.push({
    key: "competitor-analysis",
    label: "Competitor Analysis",
    status: competitorStatus,
    subGrade: inspo.missing ? 55 : SUBGRADE_FOR_STATUS[competitorStatus],
    summary: inspo.missing
      ? "Inspo link missing — soft flag, costs points but doesn't block submission."
      : "Inspo link present. Whether it's a genuine strong comparable needs human confirmation.",
    detail: inspo.missing
      ? `04-competitor-analysis.md: "did the video actually perform well, or did the creator just personally like it? A real inspo link is a genuine strong comparable, not a placeholder." No link means this can't even be checked. v0.3.0: downgraded from a hard block to a soft flag — this field is optional.`
      : `Heuristic, needs human confirmation — the checker can confirm a link is present but not whether it's "actually from a competitor/comparable brand," whether it "performed well," or which STIC beat it's demonstrating (Tim's reverse-engineer-by-beat step). Current state per the doc: competitor pull automation is C-minus quality (transcript-only), upgrading to B.`,
  })

  // 5. Creator Competencies (05-creator-competencies.md)
  findings.push({
    key: "creator-competencies",
    label: "Creator Competencies",
    status: "heuristic",
    subGrade: SUBGRADE_FOR_STATUS.heuristic,
    summary: f.creatorName.trim()
      ? `${f.creatorName} — competency tag not available to this checker; structure-fit match is a human call.`
      : "No creator name provided.",
    detail: `05-creator-competencies.md: "a script for a comedy-tagged creator that reads like a straight tutorial script is a format mismatch, independent of whether the hook itself is well-written." This grader has no access to the creator-tag roster (still mostly untagged as of the source doc — known gold creators awaiting tags: Lauren Kitch, John McAllister, Cassidy Luer, Ben Spong, Hannah Saks, Jay Crotty). Flag structure fit manually against the 10-category list until tagging lands.`,
  })

  // 6. Lessons Learned (06-lessons-learned-db.md)
  const repeatsBrandNameFirst = hook.brandNameFirstFail
  const lessonsStatus: CheckStatus = repeatsBrandNameFirst ? "fail" : stic.weakBeats.includes("Intensify") ? "fail" : "pass"
  findings.push({
    key: "lessons-learned",
    label: "Lessons Learned",
    status: lessonsStatus,
    subGrade: SUBGRADE_FOR_STATUS[lessonsStatus],
    summary: repeatsBrandNameFirst
      ? 'Repeats the documented failure pattern: "Brand-name-first hooks underperform."'
      : stic.weakBeats.includes("Intensify")
      ? "No Intensify beat detected — the most common structural failure in the DB."
      : "No documented failure pattern detected.",
    detail: repeatsBrandNameFirst
      ? `06-lessons-learned-db.md Named Failure Pattern #7: "Brand-name-first hooks underperform. Confirmed qualitatively by Tim (Mom Agenda, July 1) — working hypothesis until real watch-rate data confirms it." This script repeats that exact pattern.`
      : stic.weakBeats.includes("Intensify")
      ? `Maps to the STIC troubleshooting table: "Drop-off in the middle → Intensify — no escalation." This is flagged as the second most common structural failure across 07-hook-expertise.md's antipattern log.`
      : `No match against the 7 named failure patterns in 06-lessons-learned-db.md (dead engagement, no profile visits despite retention, no follows despite visits, no link clicks, early Tension cliff, weak Climax reaction, brand-name-first). Patterns are directionally useful, not gospel, until Acsah-pod performance data confirms them.`,
  })

  // 7. Hook Expertise (07-hook-expertise.md)
  const hookExpertiseStatus: CheckStatus = hook.brandNameFirstFail ? "fail" : stic.weakBeats.length > 0 ? "fail" : "pass"
  findings.push({
    key: "hook-expertise",
    label: "Hook Expertise",
    status: hookExpertiseStatus,
    subGrade: hook.brandNameFirstFail ? 40 : SUBGRADE_FOR_STATUS[hookExpertiseStatus],
    summary: hook.brandNameFirstFail
      ? "Fails the #1 script-killer check: brand-name-first hook."
      : stic.weakBeats.length > 0
      ? `${stic.weakBeats.length} of 4 STIC beats missing/weak: ${stic.weakBeats.join(", ")}.`
      : "Brand-recognition check clean, all 4 STIC beats detected (heuristic).",
    detail: hook.brandNameFirstFail
      ? `07-hook-expertise.md: "The #1 script-killer, above all 4 channels: a brand-name-first hook. Confirmed failure (Mom Agenda, July 1) — hard-fail regardless of everything else." Check logic stops here per the doc's own rank-ordering — fix this before anything else in this script.`
      : `07-hook-expertise.md check order: brand-recognition (clean) → STIC beat completeness → climax discipline → 4-channel hook check. ${stic.weakBeats.length > 0 ? `Weak beats detected via heuristic (contrast/escalation/resolution language): ${stic.weakBeats.join(", ")}. This is heuristic, needs human confirmation — the real check is a judgment call on whether stakes are actually named and escalated, not just whether certain words appear. Video-type note: short trend videos (6-20s) only need the hook + payoff — don't hold missing Tension/Intensify against them.` : "All 4 beats show heuristic evidence (Setup present, Tension via contrast language, Intensify via escalation language, Climax via resolution language). Still heuristic — needs human confirmation per the doc's own combine rule."}`,
  })

  return findings
}

// The editor once ran a cheap subset of these checks on every keystroke —
// brand-name-in-hook, CTA-in-script, missing inspo, hashtags over the cap —
// and painted them as inline marks, rail cards, and a "live issues" pill. It
// was a second, shakier opinion on questions the rubric already answers (H1
// judges the brand-recognition hook; H8 judges the hashtag cap), so the whole
// live layer is gone. The one survivor is the hashtag cap, which the Hashtags
// section now shows for itself — see components/script-editor-canvas.tsx.
